import type { AsyncItemStatus } from 'owa-copilot-async-chat-types';

/**
 * App-facing execution state of a Tasks Hub item, collapsed from the raw {@link AsyncItemStatus}
 * (e.g. Sydney's `TurnState` from getChats). These are the four states the Tasks Hub surfaces, each with
 * its own icon/color: a `failed` prompt, one that `needsAttention` (blocking/paused), a `completed`
 * response the user has not seen, or one still `inProgress`.
 */
export type TaskHubItemState = 'failed' | 'needsAttention' | 'completed' | 'inProgress';

/**
 * The item states ordered by urgency, most urgent first. Used to pick the single state that
 * represents a mixed backlog (e.g. the pane-selector badge color / most-urgent response type).
 */
const STATE_BY_URGENCY: readonly TaskHubItemState[] = [
    'failed',
    'needsAttention',
    'completed',
    'inProgress',
];

/**
 * Collapses a raw async status into one of the four Tasks Hub item states, or `undefined` when the
 * item should not be surfaced in the Tasks Hub (`None` has no turn yet, `Canceled` is dismissed).
 */
export function getTaskHubItemState(status: AsyncItemStatus): TaskHubItemState | undefined {
    switch (status) {
        case 'Completed':
            return 'completed';
        case 'InputRequired':
        case 'Paused':
            return 'needsAttention';
        case 'Failed':
            return 'failed';
        case 'Working':
            return 'inProgress';
        case 'None':
        case 'Canceled':
        default:
            return undefined;
    }
}

/**
 * Returns the most urgent state among the given states, or `undefined` when none are provided.
 * Urgency follows {@link STATE_BY_URGENCY}: failed > needsAttention > completed > inProgress.
 */
export function getMostUrgentState(
    states: Iterable<TaskHubItemState>
): TaskHubItemState | undefined {
    let mostUrgent: TaskHubItemState | undefined;
    let mostUrgentRank = STATE_BY_URGENCY.length;

    for (const state of states) {
        const rank = STATE_BY_URGENCY.indexOf(state);
        if (rank !== -1 && rank < mostUrgentRank) {
            mostUrgent = state;
            mostUrgentRank = rank;
            // Ranked first: nothing can outrank it.
            if (rank === 0) {
                break;
            }
        }
    }

    return mostUrgent;
}
